import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartTierChangeEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  #debouncedOnTierChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof CartTierChangeEvent) this.#onTierChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;
  /** @type {Promise<void>} */
  #operationQueue = Promise.resolve();
  #pendingOperations = 0;
  /**
   * Latest requested target variant per cart line, keyed by line number. Lets a burst of
   * rapid +/- taps on the same tier stepper (see changeLineVariant) collapse into the
   * in-flight operation for that line instead of each queuing their own.
   * @type {Map<number, number>}
   */
  #latestTierTarget = new Map();
  /** @type {Set<number>} Lines with a tier-change operation currently queued or running. */
  #tierChangeLinesInFlight = new Set();

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(ThemeEvents.cartTierChange, this.#debouncedOnTierChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(ThemeEvents.cartTierChange, this.#debouncedOnTierChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Closes the cart drawer (if open) before the Edit link's own default navigation takes the
   * shopper to the sticker design editor page. Without this, ThemeDrawer's sessionStorage
   * persistence (see assets/theme-drawer.js's persistDrawerState) carries the drawer's open
   * state across the navigation and reopens it on top of the editor on wide viewports — its
   * own restore script only skips that for narrow viewports. Doesn't preventDefault: the close
   * call's synchronous portion (through dispatchEvent, which persistDrawerState listens to)
   * completes before the browser's default navigation for this click actually runs.
   */
  onLineItemEditClick() {
    if (!this.isDrawer) return;
    this.closest('theme-drawer')?.close();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    // Resolved now, before the optimistic removal below takes the row out of the DOM —
    // this update is queued (see #enqueue) and may not actually run for a while, by which
    // point `this.refs.cartItemRows[line - 1]` could point at something else entirely.
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      lineId: cartItemRowToRemove?.dataset.key,
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Adds another of the exact same line (same variant + properties, read straight off the row —
   * see #parsePropertiesFromRow). Confirmed empirically (not documented): Shopify's cart does NOT
   * merge this into the existing line the way two plain, byte-identical property sets normally
   * would — a line carrying a property that originated as a file upload (Design/_Img_* here, see
   * assets/sticker-editor.js's own #syncDesignStateProperty) always lands as its own new line
   * instead, even resubmitted with the exact same resolved URL string. That's a fine outcome for
   * "Duplicate" on its own terms (a second, independently adjustable line of the identical design),
   * just not literally a quantity bump on the original — no changeLineVariant-style remove/head-bump
   * sequence needed either way, since nothing about the ORIGINAL line's own key/position ever
   * changes; this only ever adds.
   * @param {number} line - The line item index.
   */
  onLineItemDuplicate(line) {
    return this.#enqueue(() => this.#duplicateLineImpl(line));
  }

  /** @param {number} line */
  async #duplicateLineImpl(line) {
    const row = this.refs.cartItemRows[line - 1];
    if (!row) return;

    const variantId = Number(row.querySelector('.quantity-selector')?.getAttribute('data-variant-id'));
    const properties = this.#parsePropertiesFromRow(row);
    const lineKey = row.dataset.key;
    if (!variantId || !lineKey) return;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('duplicate-line:user-action');
    const textComponent = /** @type {TextComponent | undefined} */ (row.querySelector('text-component'));
    textComponent?.shimmer();
    this.refs.cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ id: lineKey, quantity: (Number(row.dataset.lineQuantity) || 1) + 1 }],
        promise: deferredUpdatePromise.promise,
      })
    );

    try {
      const result = await this.#addLine(variantId, 1, properties, true);
      // /cart/add.js's own response only ever describes the items just added ({items, sections}) —
      // no total_price/currency the way a full cart payload has, unlike /cart/change.js's own
      // response (see #changeLineVariantImpl's identical distinction/comment). Fetching the real
      // cart data separately here is what createCartFromAjaxResponse below actually needs.
      const cartJsonForEvent = await this.fetchCartData();
      resetShimmer(this);

      const newSectionHTML = new DOMParser().parseFromString(result.sections[this.sectionId], 'text/html');
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartJsonForEvent),
        detail: {
          sections: result.sections,
          items: result.items,
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          didError: false,
        },
      });

      await this.#preservingScrollPosition(() =>
        morphSection(this.sectionId, result.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        })
      );
      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      console.error(error);
      deferredUpdatePromise.reject(error);
      resetShimmer(this);
      sectionRenderer
        .renderSection(this.sectionId, { cache: false, mode: this.isDrawer ? 'hydration' : 'full' })
        .catch((resyncError) => console.error(resyncError));
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.name === 'AbortError' ? 'Request timed out, please try again' : error?.message || 'Failed to update bag',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Gets the section ids that need refreshing on any cart mutation (this component's
   * own section plus every other cart-items-component on the page, e.g. drawer + page).
   * @returns {string[]}
   */
  #getSectionsToUpdate() {
    const sectionsToUpdate = new Set([this.sectionId]);

    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return Array.from(sectionsToUpdate);
  }

  /**
   * Finds the element whose own scrolling moves the cart rows within the viewport, by
   * walking up from an actual row rather than from `this`. On the cart page that
   * ancestor is the page itself; in the drawer it's the `<scroll-hint>` wrapper around
   * the row list — cart-items-component wraps that wrapper (see
   * cart-items-component.liquid), so starting the walk from `this` would climb straight
   * past it and never find it. Starting from a row instead works either way, since a row
   * is always inside whichever element actually scrolls.
   * @returns {Element | null}
   */
  #findScrollContainer() {
    for (let el = this.refs.cartItemRows?.[0]; el; el = el.parentElement) {
      if (el.scrollHeight > el.clientHeight) return el;
    }
    return null;
  }

  /**
   * Runs `task`, restoring the cart's scroll position afterward if it moved.
   *
   * Replacing a row's DOM node (which happens whenever its variant/key changes, since
   * morph can't patch an element whose identity changed — see changeLineVariant) drops
   * whatever had focus inside it. The browser's own "keep the focused element in view"
   * behavior then fires again once a new element takes focus, and can land on a
   * completely different scroll position than the one the shopper actually had — e.g.
   * jumping to reveal a different cart line above the one just edited. Capturing and
   * restoring the scrollable ancestor's position around the mutation makes that
   * self-correcting regardless of which element ends up focused or why.
   * @template T
   * @param {() => Promise<T>} task
   * @returns {Promise<T>}
   */
  async #preservingScrollPosition(task) {
    const scrollable = this.#findScrollContainer();
    const scrollTop = scrollable?.scrollTop;

    try {
      return await task();
    } finally {
      // The browser's focus-follows-scroll behavior can land a frame or two after the
      // DOM mutation settles, not synchronously within it — reapply across a couple of
      // frames so a delayed correction doesn't slip past a single restore.
      if (scrollable && scrollTop !== undefined) {
        for (let i = 0; i < 3; i++) {
          await new Promise((resolve) => requestAnimationFrame(resolve));
          if (scrollable.scrollTop !== scrollTop) scrollable.scrollTop = scrollTop;
        }
      }
    }
  }

  /**
   * Runs `task` only after every previously-queued cart mutation has fully finished
   * (including its own DOM morph). Quantity changes and tier swaps both read a snapshot
   * of cart/DOM state (line keys, positions) when they start and act on it when their
   * network round trip resolves — running two of these concurrently (e.g. quickly
   * adjusting quantity on one row, then another, before the first finishes) let a later
   * step in one operation use a line key that the other operation's full-section morph
   * had already invalidated, failing with "no valid id or line parameter" and — depending
   * on where in the chain that landed — risking the cart getting stuck disabled. Queuing
   * everything through here means each operation only ever starts against settled state.
   *
   * The cart is kept disabled for the whole time ANY operation is queued or running, not
   * just the one currently executing, so a burst of clicks across several rows reads as
   * one continuous "busy" state rather than flickering enabled between each step.
   * @template T
   * @param {() => Promise<T>} task
   * @returns {Promise<T>}
   */
  #enqueue(task) {
    this.#pendingOperations++;
    if (this.#pendingOperations === 1) this.#disableCartItems();

    const run = () => task();
    const result = this.#operationQueue.then(run, run);
    // Keep the queue alive even if this task's errors weren't already handled internally —
    // one failed operation must not permanently block every operation queued after it.
    this.#operationQueue = result.then(
      () => undefined,
      () => undefined
    );

    result.finally(() => {
      this.#pendingOperations--;
      if (this.#pendingOperations === 0) this.#enableCartItems();
    });

    return result;
  }

  /**
   * fetch() with a hard timeout. Without this, a request that simply never settles (a
   * dropped connection, a proxy or extension that swallows the response, etc.) means
   * .finally() never runs, so #enableCartItems() never fires and the cart stays dimmed
   * and unresponsive indefinitely. Every cart-mutating request below goes through this
   * so that failure mode always resolves (as a rejection) within a bounded time instead.
   * @param {string} url
   * @param {RequestInit} options
   * @param {number} [timeoutMs]
   * @returns {Promise<Response>}
   */
  #fetchWithTimeout(url, options, timeoutMs = 15000) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(timeoutId));
  }

  /**
   * Guards against treating a non-JSON response body as cart JSON. A flagged IP (VPN exit
   * nodes are common targets) can get served an HTML bot-check page in place of the actual
   * response — that still resolves as a normal, non-erroring fetch, so without this check
   * the code below would try to JSON.parse an HTML document, fail with an opaque syntax
   * error, and (via the tier-change error path) fall back to re-rendering the section from
   * a follow-up request that's just as likely to get the same challenge page back, morphing
   * it into the cart and leaving it broken until a hard refresh. A real Shopify cart error
   * (e.g. insufficient stock) is still valid JSON even on a non-2xx status, so this only
   * needs to check content-type, not response.ok.
   * @param {Response} response
   */
  #assertJsonResponse(response) {
    // Shopify's own cart endpoints reply with `text/javascript`, not `application/json`
    // (a legacy of the `.js` suffix), so "isn't labeled json" isn't a safe test. `text/html`
    // is the one content-type a legitimate cart response never has — it's the actual
    // signature of a bot-check/error interstitial standing in for the real response.
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('html')) {
      throw Object.assign(new Error('Your connection was blocked before the cart could update. Please refresh the page and try again.'), {
        blockedResponse: true,
      });
    }
  }

  /**
   * Handles CartTierChangeEvent — a pcs-tier stepper (see component-cart-quantity-selector.js)
   * requesting its line be switched to a different tier variant.
   * @param {CartTierChangeEvent} event
   */
  #onTierChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { variantId, cartLine: line } = event.detail;
    if (!line || !variantId) return;

    // A tier swap is several sequential network round trips (see changeLineVariant) and
    // can take a few seconds on a slow connection, with nothing else in the UI indicating
    // progress in the meantime — shimmer the row's own price immediately so it doesn't
    // read as frozen while that's in flight.
    const lineItemRow = this.refs.cartItemRows[line - 1];
    lineItemRow?.querySelector('text-component')?.shimmer();

    this.changeLineVariant({ line, variantId });
  }

  /**
   * Parses a cart row's `data-properties` attribute into a plain object.
   * `item.properties | json` serializes as an array of [key, value] pairs, not a plain
   * object — /cart/add.js rejects an array ("expected Array to be a Hash").
   * @param {HTMLTableRowElement} row
   * @returns {Record<string, string>}
   */
  #parsePropertiesFromRow(row) {
    try {
      const parsed = row.dataset.properties ? JSON.parse(row.dataset.properties) : [];
      return Array.isArray(parsed) ? Object.fromEntries(parsed) : parsed;
    } catch {
      return {};
    }
  }

  /**
   * POSTs a single variant/quantity/properties to /cart/add.js.
   * @param {number} variantId
   * @param {number} quantity
   * @param {Record<string, string>} properties
   * @param {boolean} [withSections] - Request section HTML back (only needed on the last call of a sequence).
   */
  async #addLine(variantId, quantity, properties, withSections = false) {
    /** @type {Record<string, unknown>} */
    const body = { items: [{ id: variantId, quantity, properties }] };
    if (withSections) body.sections = this.#getSectionsToUpdate().join(',');

    const response = await this.#fetchWithTimeout(
      Theme.routes.cart_add_url,
      fetchConfig('json', { body: JSON.stringify(body) })
    );
    this.#assertJsonResponse(response);
    const json = await response.json();

    if (json.status) {
      throw Object.assign(new Error(json.message || 'Add to bag failed'), {
        cartError: true,
        errors: json.description || json.message,
      });
    }

    return json;
  }

  /**
   * Removes a cart line (by key) via /cart/change.js.
   * @param {string} lineKey
   * @param {boolean} [withSections] - Request section HTML back (only needed on the last call of a sequence).
   */
  async #removeLine(lineKey, withSections = false) {
    /** @type {Record<string, unknown>} */
    const body = { id: lineKey, quantity: 0, sections_url: window.location.pathname };
    if (withSections) body.sections = this.#getSectionsToUpdate().join(',');

    const response = await this.#fetchWithTimeout(
      Theme.routes.cart_change_url,
      fetchConfig('json', { body: JSON.stringify(body) })
    );
    this.#assertJsonResponse(response);
    const json = JSON.parse(await response.text());

    if (json.errors) {
      throw Object.assign(new Error(json.errors), { cartError: true });
    }

    return json;
  }

  /**
   * Swaps a cart line to a different variant (pcs-tier switch), preserving the line's
   * quantity, properties, AND position. Shopify's cart AJAX API has no "change this
   * line's variant" call, and — confirmed empirically, not documented — new lines are
   * always inserted at the FRONT of the cart, not appended at the end. A naive
   * add-new+remove-old would therefore push this line ahead of every line before it. To
   * avoid that, every line currently before this one is "bumped" (removed, then
   * re-added) in reverse order after the swap: since each re-add lands at the front,
   * re-adding the line closest to the target first, then working backwards to the
   * cart's first line, rebuilds the original relative order in front of the target.
   *
   * Capped at MAX_HEAD_BUMP lines and skipped entirely for bundle lines (parent/child),
   * which aren't safe to reorder independently — in either case this falls back to the
   * plain swap (line ends up at the front of the cart, same as before this existed).
   *
   * A tier stepper's +/- buttons debounce independently per tap (see
   * component-cart-quantity-selector.js), so a burst of rapid taps on the same line each
   * arrive as a separate call here. Only the first starts an operation; later ones while
   * it's still in flight just update the target in #latestTierTarget and return — the
   * running #changeLineVariantLoop picks up the newest target once it finishes its
   * current pass. Without this, N rapid taps would each queue their own full
   * add+remove(+head-bump) sequence — several sequential network round trips apiece —
   * stacking into a long wait and multiplying the odds any single round trip in that
   * chain fails.
   * @param {Object} config
   * @param {number} config.line - The line being switched.
   * @param {number} config.variantId - The tier variant to switch to.
   */
  changeLineVariant({ line, variantId }) {
    this.#latestTierTarget.set(line, variantId);
    if (this.#tierChangeLinesInFlight.has(line)) return Promise.resolve();

    this.#tierChangeLinesInFlight.add(line);
    return this.#enqueue(() => this.#changeLineVariantLoop(line)).finally(() => {
      this.#tierChangeLinesInFlight.delete(line);
    });
  }

  /**
   * Repeatedly runs #changeLineVariantImpl for `line` until #latestTierTarget has nothing
   * further queued for it, so a coalesced burst of taps (see changeLineVariant) ends up
   * on whichever tier was requested last.
   * @param {number} line
   */
  async #changeLineVariantLoop(line) {
    for (;;) {
      const variantId = this.#latestTierTarget.get(line);
      if (variantId == null) return;

      this.#latestTierTarget.delete(line);
      await this.#changeLineVariantImpl({ line, variantId });
    }
  }

  /**
   * Retries `fn` once immediately on failure. Used for steps in the head-bump sequence
   * where a line has already been removed and the only way back to a consistent cart is
   * the next call succeeding — a single transient network blip shouldn't permanently
   * cost the customer their place in the cart's order.
   * @template T
   * @param {() => Promise<T>} fn
   * @returns {Promise<T>}
   */
  async #withOneRetry(fn) {
    try {
      return await fn();
    } catch {
      return await fn();
    }
  }

  /**
   * @param {Object} config
   * @param {number} config.line - The line being switched.
   * @param {number} config.variantId - The tier variant to switch to.
   */
  async #changeLineVariantImpl({ line, variantId }) {
    const MAX_HEAD_BUMP = 8;
    // The head-bump loop below is inherently sequential (each step's cart state depends on
    // the last), so its total wait time is the SUM of every request's latency, not the max.
    // On a fast connection that's imperceptible; over a slow/VPN connection where every
    // request takes seconds instead of milliseconds, a full 8-item bump (up to 17 requests)
    // can take minutes with the cart disabled the whole time -- which reads as frozen, not
    // slow. Capping the total time the bump is allowed to run bounds that worst case: once
    // the budget is spent, whatever hasn't been bumped yet is left where the swap put it
    // (the tiered line lands above those un-bumped items, same as the plain-swap fallback
    // already does for bundles/oversized carts) instead of continuing to grind through the
    // rest of the sequence.
    const HEAD_BUMP_BUDGET_MS = 8000;
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('tier-change:user-action');

    const row = this.refs.cartItemRows[line - 1];
    if (!row) return;

    const oldLineKey = row.dataset.key;
    const quantity = Number(row.dataset.lineQuantity) || 1;
    const properties = this.#parsePropertiesFromRow(row);

    const allRows = this.refs.cartItemRows;
    const headRows = allRows.slice(0, line - 1);
    const involvesBundle =
      [row, ...headRows].some((r) => r.dataset.parentKey) ||
      allRows.some((r) => r.dataset.parentKey === oldLineKey);
    const headItems =
      involvesBundle || headRows.length > MAX_HEAD_BUMP
        ? []
        : headRows
            .map((r) => ({
              key: r.dataset.key,
              variantId: Number(r.querySelector('.quantity-selector')?.getAttribute('data-variant-id')),
              quantity: Number(r.dataset.lineQuantity) || 1,
              properties: this.#parsePropertiesFromRow(r),
            }))
            .filter((t) => t.key && t.variantId)
            .reverse();

    this.refs.cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [{ id: oldLineKey ?? '', quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    try {
      await this.#addLine(variantId, quantity, properties);

      let finalResult;
      // Full cart JSON (total_price, currency, etc.) for CartLinesUpdateEvent — /cart/change.js
      // responses already have this shape, but /cart/add.js's narrower {items, sections} shape
      // doesn't, so when the sequence ends on an add (head-bump path) it's fetched separately.
      let cartJsonForEvent;
      let headBumpBailedEarly = false;
      if (headItems.length === 0) {
        finalResult = await this.#removeLine(oldLineKey, true);
        cartJsonForEvent = finalResult;
      } else {
        await this.#withOneRetry(() => this.#removeLine(oldLineKey));

        const headBumpStart = Date.now();
        for (let i = 0; i < headItems.length; i++) {
          if (Date.now() - headBumpStart > HEAD_BUMP_BUDGET_MS) {
            headBumpBailedEarly = true;
            break;
          }

          const head = headItems[i];
          const isLast = i === headItems.length - 1;

          // Both halves of a bump are retried once: the remove because a failure here
          // would otherwise abandon the rest of the bump with the line still duplicated
          // at the front, and the re-add because the line was already removed and the
          // only way back to a consistent cart is this call succeeding.
          await this.#withOneRetry(() => this.#removeLine(head.key));
          finalResult = await this.#withOneRetry(() =>
            this.#addLine(head.variantId, head.quantity, head.properties, isLast)
          );
        }

        cartJsonForEvent = await this.fetchCartData();
      }

      resetShimmer(this);

      // When the budget above cut the bump short, `finalResult` is either missing (zero
      // head items got to run) or came from an add that wasn't the sequence's true last
      // step, so it never asked for section HTML — re-fetch and morph directly instead of
      // trying to read sections off it.
      if (headBumpBailedEarly || !finalResult?.sections?.[this.sectionId]) {
        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartJsonForEvent),
          detail: {
            sections: {},
            items: cartJsonForEvent.items,
            itemCount: cartJsonForEvent.item_count ?? 0,
            source: 'cart-items-component',
            didError: false,
          },
        });

        await this.#preservingScrollPosition(() =>
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          })
        );
      } else {
        const newSectionHTML = new DOMParser().parseFromString(finalResult.sections[this.sectionId], 'text/html');
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartJsonForEvent),
          detail: {
            sections: finalResult.sections,
            items: finalResult.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        await this.#preservingScrollPosition(() =>
          morphSection(this.sectionId, finalResult.sections[this.sectionId], {
            mode: this.isDrawer ? 'hydration' : 'full',
          })
        );
      }

      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      console.error(error);
      deferredUpdatePromise.reject(error);

      if (error?.cartError && headItems.length === 0) {
        // Only safe to attribute the error to this specific line when nothing else was
        // touched (the plain swap path). Once head-bumping is involved, several lines may
        // have been touched — re-fetch the section to reflect whatever the server actually
        // ended up with rather than guessing which line to roll back in the UI.
        this.#handleCartError(line, { errors: error.errors || error.message });
      } else {
        resetShimmer(this);
        // Best-effort resync — if this is also blocked (see #assertJsonResponse), it now
        // rejects instead of morphing a bad response into the cart (see section-renderer.js),
        // so there's nothing further to do here beyond not leaving it unhandled.
        sectionRenderer
          .renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          })
          .catch((resyncError) => console.error(resyncError));
      }

      this.dispatchEvent(
        new CartErrorEvent({
          error:
            error?.name === 'AbortError' ? 'Request timed out, please try again' : error?.message || 'Failed to update bag',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} [config.lineId] - The line's key, if already known (see onLineItemRemove) —
   *   otherwise resolved from `line`'s current position once this update actually runs.
   */
  updateQuantity(config) {
    return this.#enqueue(() => this.#updateQuantityImpl(config));
  }

  /**
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} [config.lineId]
   */
  async #updateQuantityImpl(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    // Target by line key, not position — this only runs once every earlier queued
    // operation has finished, but a position can still have shifted if one of those was a
    // removal. If the row this update was meant for is gone, there's nothing to update.
    const lineId = config.lineId ?? this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    if (!lineId) {
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      return;
    }

    const body = JSON.stringify({
      id: lineId,
      quantity: quantity,
      sections: this.#getSectionsToUpdate().join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    return this.#fetchWithTimeout(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        this.#assertJsonResponse(response);
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.name === 'AbortError' ? 'Request timed out, please try again' : error?.message || 'Failed to update bag',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Bag item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Bag item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Bag update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch bag: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
